import React from 'react';
import { SVGIcon } from '@superops/super-icons';
import EmojiRender from '@nile/components/src/Components/EmojiRender';
import { OnboardingConfig } from '../../../types/onboarding';
import { AIActivator, getTranslatedText } from '@nile/components';
import { useIntl } from 'react-intl';
import { Button, TextField } from '@superops/ui-design-system';
import { FeaturesList } from '../components/FeaturesList';
import { GET_AI_FEATURES } from '@nile/banyan/src/Graphql/Query/ai';
import { UPDATE_AI_FEATURE } from '@nile/banyan/src/Graphql/Mutations/ai';
import { CreatePrompt } from '../components/CreatePrompt';
import TriageConfigurationOnboarding from '@nile/components/src/Pages/AgentMarketplace/Configuration/TriageConfigurationOnboarding';
// Common step templates for all modules
const COMMON_STEPS = {
  WELCOME: (moduleTitle: string) => ({
    id: 'welcome-onboarding',
    title: ` ${moduleTitle}`,
    description:
      'Let me show you how Monica AI can enhance your workflow in this module.',
    order: 1,
    type: 'welcome'
  }),

  FEATURE_SHOWCASE: (
    features: Array<{
      id: string;
      title: string;
      description: string;
      gif?: string;
      icon?: string;
    }>
  ) => ({
    id: 'feature-showcase',
    title: 'Discover AI Features',
    description:
      'Explore the powerful AI capabilities available in this module.',
    order: 2,
    type: 'switchable-tabs',
    features: features,
    autoSwitchTimeout: 3000 // 3 seconds auto-switch
  }),

  ADDITIONAL_INFO: (customContent: React.ReactNode) => ({
    id: 'additional-info',
    title: 'Advanced Features',
    description:
      'Discover additional capabilities and tips for maximum productivity.',
    order: 4,
    type: 'additional-info',
    content: customContent,
    optional: true
  })
};

// Module configuration structure for easy addition
interface Features {
  id: string;
  title: string;
  description: string;
  gif?: string;
  icon?: string;
}

interface ModuleConfig {
  title: string;
  description: string;
  featureLogo?: string;
  additionalNote?: string;
  steps?: Array<{
    type: string;
    timeoutDelay?: number;
    buttonText?: string;
    data?: Array<any>;
    customComponent?: React.ComponentType<any>;
    props?: Record<string, any>; // Props to pass to custom component
    buttonConfig?: Array<{
      label?: string;
      type: string;
      featureName?: string; // For activator type
      component?: React.ComponentType<any>; // For custom type
      props?: Record<string, any>; // Props to pass to custom component
      showResponseBubble?: boolean;
      responseBubbleText?: string;
      buttonVariant?: string;
    }>;
  }>;
  customComponent?: React.ComponentType<any>;
  customComponentTimeoutDelay: number; // Delay in ms before showing custom component
  skipable?: boolean;
}

const generateOnboardingConfig = (moduleKey: string, config: ModuleConfig) => {
  const baseSteps = [
    COMMON_STEPS.WELCOME(config.title),
    COMMON_STEPS.FEATURE_SHOWCASE(config.features)
  ];

  if (config.additionalInfo) {
    baseSteps.push(COMMON_STEPS.ADDITIONAL_INFO(config.additionalInfo));
  }

  return {
    featureLogo: config.featureLogo || 'Clients',
    title: config.title,
    description: config.description,
    additionalNote: config.additionalNote,
    skipable: config.skipable ?? true,
    steps: baseSteps,
    customSteps: config.steps || [],
    customComponent: config.customComponent,
    customComponentTimeoutDelay: config.customComponentTimeoutDelay
  };
};

export const BubbleText: React.FC<{ text: React.ReactNode }> = ({ text }) => (
  <div className="flex justify-end mb10">
    <div className="ai-bubble p10 w-max-content">{text}</div>
  </div>
);

const MODULE_CONFIGS: Record<string, ModuleConfig> = {
  PATCH_AGENT: {
    featureLogo: 'Clients',
    title: 'patch.agent',
    description: 'rmm.patch.onboarding.description',
    steps: [
      {
        type: 'feature-showcase',
        timeoutDelay: 3000,
        data: [
          {
            title: 'rmm.patch.onboarding.stabilitySnapshot',
            gif: 'Patch/AIPatchStability.svg'
          },
          {
            title: 'rmm.patch.onboarding.vendorAdvisories',
            gif: 'Patch/AIPatchknownIssue.svg'
          },
          {
            title: 'rmm.patch.onboarding.communitySentiment',
            gif: 'Patch/AIPatchSentiment.svg'
          }
        ],
        buttonConfig: [
          // {
          //   label: 'Start Agent',
          //   type: 'activator',
          //   featureName: 'PATCH_AGENT'
          // }
        ]
      }
    ],
    customComponentTimeoutDelay: 2000
  },
  TICKET_CONVERSATION_SUMMARIZE: {
    featureLogo: 'AI_TICKET_SUMMARY',
    title: 'ai.onboarding.ticketsummarizer.title',
    description: 'ai.onboarding.ticketsummarizer.description',
    steps: [
      {
        type: 'feature-showcase',
        timeoutDelay: 3000,
        data: [
          {
            title: 'ai.onboarding.ticketsummarizer.steps.title1',
            gif: 'Summarizer/summarizer-1.svg'
          },
          {
            title: 'ai.onboarding.ticketsummarizer.steps.title2',
            gif: 'Summarizer/summarizer-2.svg'
          },
          {
            title: 'ai.onboarding.ticketsummarizer.steps.title3',
            gif: null
          }
        ],
        buttonConfig: [
          {
            label: 'onboarding.agent.startagent',
            type: 'activator',
            featureName: 'TICKET_CONVERSATION_SUMMARIZE'
          }
        ]
      }
    ],
    customComponentTimeoutDelay: 2000
  },
  SIDE_CONVERSATION_COMPOSE: {
    featureLogo: 'AI_COMPOSE',
    title: 'Side Conversation Composer agent',
    description:
      "Hi there 👋 I'm Monica, your AI assistant for composing side conversations.",
    steps: [
      {
        type: 'feature-showcase',
        timeoutDelay: 3000,
        data: [
          {
            title: 'ai.onboarding.sideconversation.steps.title1',
            gif: 'Side-Conversation/summarizer-1.svg'
          },
          {
            title: 'ai.onboarding.sideconversation.steps.title2',
            gif: 'Side-Conversation/summarizer-2.svg'
          }
        ],
        buttonConfig: [
          {
            label: 'onboarding.agent.startagent',
            type: 'activator',
            featureName: 'SIDE_CONVERSATION_COMPOSE'
          }
        ]
      }
    ],
    customComponentTimeoutDelay: 2000
  },
  WORKLOG_SUMMARIZATION: {
    featureLogo: 'AI_WORKLOG',
    title: 'ai.onboarding.worklogautomation.title',
    description: 'ai.onboarding.worklogautomation.description',
    steps: [
      {
        type: 'feature-showcase',
        timeoutDelay: 3000,
        data: [
          {
            title: 'ai.onboarding.worklogautomation.steps.title1',
            gif: 'Worklog-summarizer/w1.svg'
          },
          {
            title: 'ai.onboarding.worklogautomation.steps.title2',
            gif: 'Worklog-summarizer/w2.svg'
          },
          {
            title: 'ai.onboarding.worklogautomation.steps.title3',
            gif: null
          }
        ],
        buttonConfig: [
          {
            label: 'onboarding.agent.startagent',
            type: 'activator',
            featureName: 'WORKLOG_SUMMARIZATION'
          }
        ]
      }
    ],
    customComponentTimeoutDelay: 2000
  },
  TICKETS_SUMMARY_BY_CATEGORY: {
    featureLogo: 'AI_ANALYTICS',
    title: 'ai.onboarding.ticketsummarybycategory.title',
    description: 'ai.onboarding.ticketsummarybycategory.description',
    steps: [
      {
        type: 'feature-showcase',
        timeoutDelay: 3000,
        data: [
          {
            title: 'ai.onboarding.ticketsummarybycategory.steps.title1',
            gif: 'Ticket-efficiency/Ticket-eff-1.svg'
          },
          {
            title: 'ai.onboarding.ticketsummarybycategory.steps.title2',
            gif: 'Ticket-efficiency/Ticket-eff-2.svg'
          },
          {
            title: 'ai.onboarding.ticketsummarybycategory.steps.title3',
            gif: null
          }
        ],
        buttonConfig: []
      }
    ],
    customComponentTimeoutDelay: 2000
  },
  WORKLOG_NOTE_COMPOSE: {
    featureLogo: 'AI_NOTE',
    title: 'ai.onboarding.worklognotecompose.title',
    description: 'ai.onboarding.worklognotecompose.description',
    steps: [
      {
        type: 'feature-showcase',
        timeoutDelay: 3000,
        data: [
          {
            title: 'ai.onboarding.worklognotecompose.steps.title1',
            gif: 'Worklog-Note-Compose/worklog-note-gen1.svg'
          },
          {
            title: 'ai.onboarding.worklognotecompose.steps.title2',
            gif: 'Worklog-Note-Compose/worklog-note-gen2.svg'
          }
        ],
        buttonConfig: [
          {
            label: 'onboarding.agent.startagent',
            type: 'custom',
            buttonVariant: 'aiPrimaryFill',
            featureName: 'WORKLOG_NOTE_COMPOSE'
          }
        ]
      }
    ],
    customComponentTimeoutDelay: 2000
  },
  RECOMMENDED_SOLUTION: {
    featureLogo: 'Clients',
    title: 'ai.onboarding.recommendedsolution.title',
    description: 'ai.onboarding.recommendedsolution.description',
    steps: [
      {
        type: 'feature-showcase',
        timeoutDelay: 3000,
        data: [
          {
            title: 'ai.onboarding.recommendedsolution.steps.title1',
            gif: 'Reccommended-solution/r-s-1.svg'
          },
          {
            title: 'ai.onboarding.recommendedsolution.steps.title2',
            gif: 'Reccommended-solution/r-s-2.svg'
          },
          {
            title: 'ai.onboarding.recommendedsolution.steps.title3',
            gif: null
          }
        ],
        buttonConfig: [
          // {
          //   label: 'onboarding.agent.startagent',
          //   type: 'activator',
          //   featureName: 'RECOMMENDED_SOLUTION'
          // },
          // {
          //   label: 'Explore Further',
          //   type: 'custom',
          //   showResponseBubble: true,
          //   responseBubbleText: 'Explore Further Bubble',
          //   component: RecommendedSolutionsCustomComponent,
          //   props: { featureName: 'RECOMMENDED_SOLUTION' }
          // }
          {
            label: 'common.continue',
            type: 'nextstep',
            showResponseBubble: true,
            responseBubbleText: 'Explore Further Bubble'
          }
        ]
      },
      {
        type: 'feature-functionality',
        customComponent: FeaturesList,
        props: {
          featureType: 'RECOMMENDED_SOLUTION',
          config: {
            useSubFeatures: true,
            showBubble: false,
            hasPromptInput: false,
            useAIFeaturesHook: true
          }
        },
        buttonConfig: [
          {
            label: 'onboarding.agent.startagent',
            type: 'activator',
            featureName: 'RECOMMENDED_SOLUTION'
          }
        ]
      }
    ],
    // customComponent: CreatePromptComponent,
    customComponentTimeoutDelay: 2000
  },
  REAL_TIME_ALERTS: {
    featureLogo: 'AI_ALERTS',
    title: 'Real-time Alerts agent',
    description:
      "Hi there 👋 I'm Monica, your AI assistant for intelligent real-time alerts.",
    steps: [
      {
        type: 'feature-showcase',
        timeoutDelay: 3000,
        data: [
          {
            title: ' Get intelligent real-time notifications.',
            gif: 'Splashtop.png'
          },
          {
            title: 'Prioritize alerts based on severity.',
            gif: 'Teamviewer.png'
          },
          {
            title: ' Reduce alert fatigue with smart filtering.',
            gif: 'Webroot.png'
          }
        ],
        buttonConfig: [
          {
            label: 'onboarding.agent.startagent',
            type: 'activator',
            featureName: 'REAL_TIME_ALERTS'
          }
        ]
      }
    ],
    customComponentTimeoutDelay: 2000
  },
  WEB_SEARCH: {
    featureLogo: 'AI_SEARCH',
    title: 'Web Search agent',
    description:
      "Hi there 👋 I'm Monica, your AI assistant for intelligent web searches.",
    steps: [
      {
        type: 'feature-showcase',
        timeoutDelay: 3000,
        data: [
          {
            title: 'Perform contextual web searches for solutions.',
            gif: 'Splashtop.png'
          },
          {
            title: 'Filter and curate relevant search results.',
            gif: 'Teamviewer.png'
          },
          {
            title: 'Find external resources and documentation.',
            gif: 'Webroot.png'
          }
        ],
        buttonConfig: [
          {
            label: 'onboarding.agent.startagent',
            type: 'activator',
            featureName: 'WEB_SEARCH'
          }
        ]
      }
    ],
    customComponentTimeoutDelay: 2000
  },
  SIMILAR_TICKETS: {
    featureLogo: 'AI_SUGGESTIONS',
    title: 'ai.onboarding.similartickets.title',
    description: 'ai.onboarding.similartickets.description',
    steps: [
      {
        type: 'feature-showcase',
        timeoutDelay: 3000,
        data: [
          {
            title: 'ai.onboarding.similartickets.steps.title1',
            gif: 'Similar-tickets/s1.svg'
          },
          {
            title: 'ai.onboarding.similartickets.steps.title2',
            gif: 'Similar-tickets/s2.svg'
          },
          {
            title: 'ai.onboarding.similartickets.steps.title3',
            gif: 'Similar-tickets/s3.svg'
          }
        ],
        buttonConfig: [
          {
            label: 'onboarding.agent.startagent',
            type: 'activator',
            featureName: 'SIMILAR_TICKETS'
          }
        ]
      }
    ],
    customComponentTimeoutDelay: 2000
  },
  SCRIPT_GENERATION: {
    featureLogo: 'AIWand',
    title: 'ai.onboarding.scriptgeneration.title',
    description: 'ai.onboarding.scriptgeneration.description',
    steps: [
      {
        type: 'feature-showcase',
        timeoutDelay: 3000,
        data: [
          {
            title: 'ai.onboarding.scriptgeneration.steps.title1',
            gif: 'Script-Generation/Script-1.svg'
          },
          {
            title: 'ai.onboarding.scriptgeneration.steps.title2',
            gif: 'Script-Generation/Script-2.svg'
          }
        ],
        buttonConfig: [
          {
            label: 'onboarding.agent.startagent',
            type: 'custom',
            buttonVariant: 'aiPrimaryFill',
            featureName: 'SCRIPT_GENERATION'
          }
        ]
      }
    ],
    customComponentTimeoutDelay: 2000
  },
  KNOWLEDGE_BASE: {
    featureLogo: 'AI_KB',
    title: 'ai.onboarding.kbassistant.title',
    description: 'ai.onboarding.kbassistant.description',
    steps: [
      {
        type: 'feature-showcase',
        timeoutDelay: 3000,
        data: [
          {
            title: 'ai.onboarding.kbassistant.steps.title1',
            gif: 'KB/kb-1.svg'
          },
          {
            title: 'ai.onboarding.kbassistant.steps.title2',
            gif: 'KB/kb-2.svg'
          }
        ],
        buttonConfig: [
          {
            label: 'onboarding.agent.startagent',
            type: 'activator',
            featureName: 'KNOWLEDGE_BASE'
          }
        ]
      }
    ],
    customComponentTimeoutDelay: 2000
  },
  // TICKET_DEFLECTION: {
  //   featureLogo: 'AI_DEFLECTION',
  //   title: 'Ticket Deflection agent',
  //   description:
  //     "Hi there 👋 I'm Monica, your AI assistant for proactive ticket deflection.",
  //   steps: [
  //     {
  //       type: 'feature-showcase',
  //       timeoutDelay: 3000,
  //       data: [
  //         {
  //           title:
  //             ' Automatically deflect common tickets with self-service options.',
  //           gif: 'Splashtop.png'
  //         },
  //         {
  //           title: ' Reduce ticket volume with intelligent routing.',
  //           gif: 'Teamviewer.png'
  //         },
  //         {
  //           title: ' Improve user satisfaction with instant solutions.',
  //           gif: 'Webroot.png'
  //         }
  //       ],
  //       buttonConfig: [
  //         {
  //           label: 'onboarding.agent.startagent',
  //           type: 'activator',
  //           featureName: 'TICKET_DEFLECTION'
  //         }
  //       ]
  //     }
  //   ],
  //   customComponentTimeoutDelay: 2000
  // },
  PROMPT_BUILDER: {
    featureLogo: 'ScriptColoured',
    title: 'ai.onboarding.promptbuilder.title',
    description: 'ai.onboarding.promptbuilder.description',
    steps: [
      {
        type: 'feature-showcase',
        timeoutDelay: 3000,
        data: [
          {
            title: 'ai.onboarding.promptbuilder.steps.title1',
            gif: 'Prompt-builder-agent/pba-1.svg'
          },
          {
            title: 'ai.onboarding.promptbuilder.steps.title2',
            gif: 'Prompt-builder-agent/pba-2.svg'
          },
          {
            title: 'ai.onboarding.promptbuilder.steps.title3',
            gif: null
          }
        ],
        buttonConfig: [
          {
            label: 'common.continue',
            type: 'nextstep',
            showResponseBubble: true,
            responseBubbleText: 'Continue Bubble'
          }
        ]
      },
      {
        type: 'feature-functionality',
        customComponent: FeaturesList,
        props: {
          featureType: 'PROMPT_BUILDER',
          config: {
            useSubFeatures: true,
            showBubble: true,
            title: 'Prompt Features',
            useAIFeaturesHook: true,
            apiConfig: {
              fetchFeatures: GET_AI_FEATURES,
              updateFeature: UPDATE_AI_FEATURE
            },
            customComponent: CreatePrompt
          }
        },
        buttonConfig: [
          {
            label: 'onboarding.agent.startagent',
            type: 'custom',
            buttonVariant: 'aiPrimaryFill',
            featureName: 'PROMPT_BUILDER'
          }
        ]
      }
    ],
    // customComponent: AiOnboardingCreatePromptComponent,
    customComponentTimeoutDelay: 2000
  },
  TICKET_TRIAGE: {
    featureLogo: 'Clients',
    title: 'ai.onboarding.tickettriage.title',
    description: 'ai.onboarding.tickettriage.description',
    additionalNote: 'ai.onboarding.tickettriage.additionalnote',
    steps: [
      {
        type: 'feature-showcase',
        timeoutDelay: 3000,
        data: [
          {
            title: 'ai.onboarding.triage.steps.title1',

            gif: 'Triaging/triage-1.svg'
          },
          {
            title: 'ai.onboarding.triage.steps.title2',
            gif: 'Triaging/triage-2.svg'
          },
          {
            title: 'ai.onboarding.triage.steps.title3'
          }
        ],
        buttonConfig: [
          {
            label: 'common.continue',
            type: 'nextstep',
            showResponseBubble: true,
            responseBubbleText: 'Explore Further Bubble'
          }
        ]
      },
      {
        type: 'feature-functionality',
        customComponent: TriageConfigurationOnboarding,
        props: {
          featureType: 'RECOMMENDED_SOLUTION',
          config: {
            showBubble: false,
            hasPromptInput: false,
            useAIFeaturesHook: true
          }
        }
      }
    ],
    // customComponent: CreatePromptComponent,
    customComponentTimeoutDelay: 2000
  },
  // NESTED_SAMPLE: {
  //   featureLogo: 'FireWall',
  //   title: 'Nested type agent',
  //   description:
  //     "Hey there 👋 I'm your new Prompt Builder Agent, here to help your technicians craft responses that are consistent and tailored to your MSP's tone of voice.",
  //   steps: [
  //     {
  //       type: 'feature-showcase',
  //       timeoutDelay: 3000,
  //       data: [
  //         {
  //           id: 'scan',
  //           title: "1. I ENSURE REPLIES MATCH YOUR COMPANY'S STYLE ",
  //           description: 'scan ticket history and suggest potential solutions',
  //           gif: 'Splashtop.png',
  //           icon: 'AI_SUGGESTIONS'
  //         },
  //         {
  //           id: 'apply',
  //           title: '2. CREATE CUSTOM PROMPTS',
  //           description: 'apply the solution directly as a ticket response',
  //           gif: 'Teamviewer.png',
  //           icon: 'AI_SUGGESTIONS'
  //         },
  //         {
  //           id: 'learn',
  //           title: '3. READY-TO-GO EXAMPLE',
  //           description: 'verify the source of the suggestion before sending',
  //           gif: 'Webroot.png',
  //           icon: 'AI_SUGGESTIONS'
  //         }
  //       ],
  //       buttonConfig: [
  //         // {
  //         //   label: 'onboarding.agent.startagent',
  //         //   type: 'activator',
  //         //   featureName: 'RECOMMENDED_SOLUTION'
  //         // },
  //         // {
  //         //   label: 'Explore Further',
  //         //   type: 'custom',
  //         //   showResponseBubble: true,
  //         //   responseBubbleText: 'Explore Further Bubble',
  //         //   component: CreatePromptComponent,
  //         //   props: { featureName: 'RECOMMENDED_SOLUTION' }
  //         // }
  //         {
  //           label: 'Explore Further v2',
  //           type: 'nextstep',
  //           showResponseBubble: true,
  //           responseBubbleText: 'Explore Further Bubble'
  //         }
  //       ]
  //     },
  //     {
  //       type: 'nested-choice',
  //       timeoutDelay: 3000,
  //       data: ['choice 1', 'choice 2'],
  //       buttonConfig: [
  //         {
  //           label: 'Explore Further v3',
  //           type: 'nextstep',
  //           showResponseBubble: true,
  //           responseBubbleText: 'Explore Further Bubble'
  //         }
  //       ]
  //     },
  //     {
  //       type: 'nested-choice',
  //       timeoutDelay: 3000,
  //       data: ['choice 2.1', 'choice 2.2'],
  //       buttonConfig: [
  //         {
  //           label: 'Explore Further v3',
  //           type: 'nextstep',
  //           showResponseBubble: true,
  //           responseBubbleText: 'Explore Further Bubble'
  //         }
  //       ]
  //     }
  //   ],
  //   // customComponent: CreatePromptComponent,
  //   customComponentTimeoutDelay: 2000
  // },
  WORKLOG_AUTOMATION: {
    featureLogo: 'AI_WORKLOG',
    title: 'ai.onboarding.worklogautomation.title',
    description: 'ai.onboarding.worklogautomation.description',
    steps: [
      {
        type: 'feature-showcase',
        timeoutDelay: 3000,
        data: [
          {
            title: 'ai.onboarding.worklogautomation.steps.title1',
            gif: 'Worklog-note-summarizer/worklog-note-summarizer-1.svg'
          },
          {
            title: '',
            gif: 'Worklog-note-summarizer/worklog-note-summarizer-2.svg'
          }
        ],
        buttonConfig: [
          {
            label: 'onboarding.agent.startagent',
            type: 'activator',
            featureName: 'WORKLOG_SUMMARIZATION'
          }
        ]
      }
    ],
    customComponentTimeoutDelay: 2000
  }
};

export const ONBOARDING_CONFIG: OnboardingConfig = Object.keys(
  MODULE_CONFIGS
).reduce((acc, moduleKey) => {
  acc[moduleKey] = generateOnboardingConfig(
    moduleKey,
    MODULE_CONFIGS[moduleKey]
  );
  return acc;
}, {} as OnboardingConfig);
